import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Search, Navigation } from "lucide-react";
import { PageHeader, Loading } from "../components/Layout";
import { FictionalBadge } from "../components/Badges";
import { api } from "../lib/api";
import { MyLocationButton, locLabel, locInput } from "../components/search/MyLocationButton";
import { EmergencyButton } from "../components/EmergencyButton";
import { ClinicLogo } from "../components/ClinicLogo";

export const REGIONS = ["Region Hovedstaden", "Region Sjælland", "Region Syddanmark", "Region Midtjylland", "Region Nordjylland"];
export const CLINIC_TYPE = { CLINIC: "Dyreklinik", HOSPITAL: "Dyrehospital", LARGE_ANIMAL: "Heste-/landbrugspraksis", EQUINE_OR_FARM: "Heste-/landbrugspraksis" };
export const addressText = (l) => (l.address ? `${l.address}, ${l.postal_code} ${l.city}` : `Nær ${l.nearest_postal_code || ""} ${l.nearest_city || ""} (adresse ikke oplyst)`);
const PAGE = 30;

const ClinicCard = ({ c }) => (
  <Link to={`/dyreklinikker/${c.slug}`} className="card-vp p-5 hover:shadow-md transition-shadow" data-testid={`clinic-card-${c.slug}`}>
    <div className="flex flex-wrap items-center gap-3"><ClinicLogo url={c.logo_url} name={c.name} size="sm" testid={`clinic-logo-${c.slug}`} /><h2 className="font-heading text-lg font-bold text-[#153C3A]">{c.name}</h2>{c.is_fictional && <FictionalBadge testid={`clinic-fictional-${c.slug}`} />}</div>
    <p className="text-xs text-[#52706D] mt-1">{CLINIC_TYPE[c.clinic_type] || "Dyreklinik"}{c.chain ? ` · ${c.chain}` : ""} · {c.region || "Region ikke oplyst"}</p>
    <ul className="mt-2 space-y-1">
      {c.locations.map((l) => <li key={l.id} className="text-sm text-[#52706D] flex items-center gap-1"><MapPin className="h-3.5 w-3.5 shrink-0" />{addressText(l)}</li>)}
    </ul>
    {c.distance_km != null && <p className="text-xs text-[#087F72] mt-1 flex items-center gap-1"><Navigation className="h-3 w-3" />{c.distance_km.toLocaleString("da-DK")} km</p>}
    <p className="text-xs font-semibold text-[#087F72] mt-3" data-testid={`clinic-price-count-${c.slug}`}>{c.price_count > 0 ? `${c.price_count} verificerede priser` : "Ingen verificerede priser endnu"}<span className="font-normal text-[#52706D]"> · {c.website ? "Hjemmeside" : "Ingen hjemmeside registreret"}</span></p>
  </Link>
);

export default function Clinics() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [region, setRegion] = useState(sp.get("region") || "");
  const [items, setItems] = useState(null);
  const [n, setN] = useState(PAGE);
  const load = (qq = q, rr = region) => {
    setItems(null); setN(PAGE);
    setSp(Object.fromEntries(Object.entries({ q: qq, region: rr }).filter(([, v]) => v)));
    api.get("/public/clinics", { params: { q: qq || undefined, region: rr || undefined } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="clinics-page">
      <PageHeader eyebrow="Klinikoversigt" title="Find dyreklinikker og dyrehospitaler i Danmark">Find dyrlæger, dyreklinikker og dyrehospitaler med adresse, hjemmeside og prisstatus. Priser vises kun, når de er verificeret fra klinikkens egen prisliste.</PageHeader>
      <div className="container-vp py-10">
        <form className="card-vp p-4 grid gap-3 sm:grid-cols-[1fr_240px_auto]" onSubmit={(e) => { e.preventDefault(); load(); }} role="search">
          <input className="field-vp" placeholder="Kliniknavn, by eller postnummer" value={locLabel(q)} onChange={(e) => { const v = e.target.value; setQ((p) => locInput(p, v)); }} aria-label="Søg" data-testid="clinics-search-input" />
          <select className="field-vp" value={region} onChange={(e) => { setRegion(e.target.value); load(q, e.target.value); }} aria-label="Region" data-testid="clinics-region-select">
            <option value="">Alle regioner</option>
            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button className="btn-primary" type="submit" data-testid="clinics-search-button"><Search className="h-4 w-4" />Søg</button>
        </form>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <MyLocationButton testid="clinics-my-location-button" onLocate={(c) => { setQ(c); load(c, region); }} />
          <EmergencyButton testid="clinics-emergency-button" />
        </div>
        {!items ? <Loading /> : (
          <>
            <p className="text-sm text-[#52706D] mt-6" data-testid="clinics-count">{items.length} klinikker{items.some((c) => c.distance_km != null) ? " – nærmeste først" : ""}</p>
            <div className="grid gap-4 md:grid-cols-2 mt-3">
              {items.slice(0, n).map((c) => <ClinicCard key={c.id} c={c} />)}
              {items.length === 0 && <p className="text-[#52706D]" data-testid="clinics-empty">Ingen klinikker matcher søgningen.</p>}
            </div>
            {items.length > n && <button className="btn-outline mt-6 mx-auto block" onClick={() => setN(n + PAGE)} data-testid="clinics-show-more">Vis flere ({items.length - n})</button>}
          </>
        )}
      </div>
    </div>
  );
}
